import type { ChatStatus, SourceDocumentUIPart, SourceUrlUIPart } from "ai";
import type { NoxusUIMessage } from "./noxus-message-types";
import { useMemo, useCallback, memo, type MouseEventHandler } from "react";
import { useTranslations } from "next-intl";
import { RefreshCcwIcon, MessageCircle } from "lucide-react";
import {
  Message,
  MessageAttachment,
  MessageAttachments,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "../ai-elements/reasoning";
import { CHAIN_TEXT_SIZE } from "../ai-elements/chain-of-thought";
import { ChainEntry } from "./ChainEntry";
import { ThinkingLine, thinkingLabel } from "./ThinkingLine";
import {
  answerStarted as hasAnswerStarted,
  getChainHeaderText,
  getChainHeaderStatus,
  getChainPreview,
  isChainablePart,
  reasoningTitle,
  ACTIVE_TOOL_STATES,
} from "./chainHeaderText";
import { defaultRehypePlugins, type StreamdownProps } from "streamdown";
import {
  CitationSourcesContext,
  CitationComponent,
  StandaloneCitation,
  rehypeCitationMarkers,
  preprocessCitationParts,
} from "./citationPlugin";
import { Button } from "../shadcn/button";
import { ManageUsageControlsButton } from "./ManageUsageControlsButton";
import {
  BaseTool,
  CodeExecutionToolCall,
  ToolPartBase,
  ToolApprovalCard,
  STEP_DETAIL_INDENT,
  type ToolApprovalPart,
} from "./ToolComponents";
import { cn } from "@/lib/utils";
import {
  isHiddenToolPart,
  resolveToolDisplay,
  scriptCallPart,
} from "./toolDisplay";
import { isActionablePendingApproval } from "./approval/approvalGate";
import { WorkflowRunTool } from "./WorkflowRunTool";
import { SmoothMessageResponse } from "./SmoothMessageResponse";
import { triggerPylonMessage } from "@/utils/pylon";
import { useSpotUser } from "@/utils/spotUser";
import { Alert, AlertTitle, AlertDescription } from "../shadcn/alert";
import { ImageThumbnails } from "./ImageThumbnails";
import {
  SourceImageAttachments,
  isSourceImagePart,
} from "./SourceImageAttachments";
import { NoxusMessageActions } from "./NoxusMessageActions";
import { TestThisReplyAction } from "./TestThisReplyAction";
import {
  groupConsecutiveParts,
  findDataPart,
  isEmptyThought,
  isNarrationPart,
  type GroupedPart,
  type MessagePart,
} from "./utils";
import {
  renderDataPart,
  TOOL_METADATA_DATA_PARTS,
} from "./data-part-renderers";
import { MinimalToolsIndicator } from "./MinimalToolsIndicator";
import {
  useChatDisplayOptionsContext,
  type CitationStyle,
} from "./useChatDisplayOptions";
import { useMcpPrefixMeta, type McpPrefixMeta } from "./mcpToolIcon";
import type { SenderRefSchema } from "@/generated";
import { Tooltip, TooltipContent, TooltipTrigger } from "../shadcn/tooltip";
import { QuestionToolRow } from "./questions/QuestionToolRow";
import { QUESTION_PART_TYPE } from "./questions/questionTypes";

const CHANNEL_ICONS: Record<string, string> = {
  slack:
    "https://storage.googleapis.com/image-storage-spot-manual/logos/slack.svg",
  teams:
    "https://storage.googleapis.com/image-storage-spot-manual/logos/msteams.png",
  whatsapp:
    "https://storage.googleapis.com/image-storage-spot-manual/logos/whatsapp.svg",
};

function ExternalSenderBadge({
  participant,
  channelType,
}: {
  participant: { [key: string]: unknown };
  channelType?: string;
}) {
  const platform =
    (participant.channel_type as string | undefined) || channelType;
  const icon = platform ? CHANNEL_ICONS[platform] : undefined;
  const displayName = participant.display_name as string | undefined;
  const email = participant.email as string | undefined;
  const externalId = participant.external_id as string | undefined;
  const avatarUrl = participant.avatar_url as string | undefined;
  const platformMetadata = (participant.platform_metadata ?? {}) as Record<
    string,
    string
  >;
  const name = displayName || email || externalId || "Unknown";

  // Tooltip lists every field the agent has on this participant —
  // i.e. the same data folded into the <<msg sender=…>> message header
  // and exposed via context_variables.user.
  const tooltipRows: { label: string; value: string }[] = [];
  if (displayName) tooltipRows.push({ label: "Name", value: displayName });
  if (email) tooltipRows.push({ label: "Email", value: email });
  if (externalId) tooltipRows.push({ label: "ID", value: externalId });
  if (platform) tooltipRows.push({ label: "Channel", value: platform });
  if (avatarUrl) tooltipRows.push({ label: "Avatar", value: avatarUrl });
  for (const [k, v] of Object.entries(platformMetadata)) {
    if (typeof v === "string" && v) tooltipRows.push({ label: k, value: v });
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1.5 mb-1 text-xs text-muted-foreground justify-end cursor-help">
          {icon && <img src={icon} alt={platform} className="w-3.5 h-3.5" />}
          <span className="font-medium text-foreground">{name}</span>
          {email && displayName && (
            <span className="text-muted-foreground">({email})</span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="left" align="start" className="max-w-xs">
        <div className="flex flex-col gap-1 py-0.5">
          <div className="font-medium text-[11px] uppercase tracking-wide opacity-70">
            Agent sees
          </div>
          {tooltipRows.length === 0 ? (
            <div className="text-xs">No participant info available</div>
          ) : (
            tooltipRows.map((row) => (
              <div key={row.label} className="flex gap-1.5 text-xs">
                <span className="opacity-70">{row.label}:</span>
                <span className="break-all">{row.value}</span>
              </div>
            ))
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Render a tool part. All display decisions (icon, titles, which component,
 * whether to hide) come from ``resolveToolDisplay`` — the single source of
 * truth for tool display. Only the two rich renderers (workflow card, code
 * execution) get dedicated components; everything else is a ``BaseTool``.
 */
function renderToolPart(
  toolPart: ToolPartBase,
  allParts: MessagePart[],
  key: string,
  groupId: string,
  mcpPrefixes: Map<string, McpPrefixMeta>,
  isPublic: boolean
): React.ReactElement | null {
  const info = resolveToolDisplay(toolPart, mcpPrefixes, allParts);

  if (info.renderer === "hidden") return null;

  if (info.renderer === "workflow") {
    const meta = findDataPart(
      allParts,
      "data-run_workflow",
      toolPart.toolCallId
    );
    return (
      <WorkflowRunTool
        key={key}
        metadata={meta?.data}
        state={toolPart.state}
        input={toolPart.input}
        output={toolPart.output}
        groupId={groupId}
        toolCallId={toolPart.toolCallId}
        isPublic={isPublic}
        callProviderMetadata={toolPart.callProviderMetadata}
      />
    );
  }

  if (info.renderer === "code") {
    const calls =
      findDataPart(allParts, "data-code_mode_calls", toolPart.toolCallId)?.data
        .calls ?? [];
    const rows = calls
      .map((call) =>
        renderToolPart(
          scriptCallPart(call),
          allParts,
          call.tool_call_id,
          groupId,
          mcpPrefixes,
          isPublic
        )
      )
      .filter((row) => row !== null);
    return <CodeExecutionToolCall key={key} part={toolPart} calls={rows} />;
  }

  return <BaseTool key={key} part={toolPart} info={info} />;
}

function rendersSomething(
  part: GroupedPart,
  citationStyle: CitationStyle,
  mcpPrefixes: Map<string, McpPrefixMeta>
): boolean {
  if (part.type === "text") return part.text.trim() !== "";
  if (part.type === "source-group") return citationStyle !== "hidden";
  if (part.type === QUESTION_PART_TYPE) return true;
  if (part.type.startsWith("tool-") && "state" in part) {
    return (
      isActionablePendingApproval(part) ||
      resolveToolDisplay(part as ToolPartBase, mcpPrefixes).renderer !==
        "hidden"
    );
  }
  return true;
}

const AGENT_PROSE_SPACING = "my-1.5";

type SharedPartProps = {
  role: NoxusUIMessage["role"];
  allParts: MessagePart[];
  groupId: string;
  conversationId: string;
  id: string;
  publicMetadata?: { group_id: string; token: string; deployment_id?: string };
  senderRef?: SenderRefSchema | null;
  citationComponents: StreamdownProps["components"];
  citationRehypePlugins: StreamdownProps["rehypePlugins"];
  // Only on the latest message: an earlier turn cannot be regenerated.
  regenerate?: (options?: { messageId?: string }) => any;
  handlePylon: MouseEventHandler<HTMLButtonElement>;
  mcpPrefixes: Map<string, McpPrefixMeta>;
};

const RenderedPart = memo(function RenderedPart({
  part,
  index: i,
  shouldAnimate,
  role,
  allParts,
  groupId,
  conversationId,
  id,
  publicMetadata,
  senderRef,
  citationComponents,
  citationRehypePlugins,
  regenerate,
  handlePylon,
  mcpPrefixes,
}: SharedPartProps & {
  part: GroupedPart;
  index: number;
  shouldAnimate: boolean;
}) {
  const {
    hideReasoning,
    citationStyle,
    omitCitationQuotes,
    toolDisplayPreset,
    groupIntoChainOfThought,
    hideErrorBanners,
  } = useChatDisplayOptionsContext();
  const { spotTenant } = useSpotUser();
  const t = useTranslations("widget.status");

  if (part.type === QUESTION_PART_TYPE) {
    return <QuestionToolRow part={part as ToolPartBase} />;
  }

  if (
    part.type.startsWith("tool-") &&
    "state" in part &&
    isActionablePendingApproval(part)
  ) {
    return <ToolApprovalCard part={part as ToolApprovalPart} />;
  }

  switch (part.type) {
    case "text": {
      if (role === "user") {
        let displayText = part.text;
        if (senderRef?.external_participant) {
          displayText = displayText.replace(/^\[.*?\]:\s*/, "");
        }
        return (
          <MessageResponse
            className="leading-relaxed"
            components={citationComponents}
            rehypePlugins={citationRehypePlugins}
          >
            {displayText}
          </MessageResponse>
        );
      }
      return (
        <SmoothMessageResponse
          className={cn("leading-relaxed", AGENT_PROSE_SPACING)}
          content={part.text}
          shouldAnimate={shouldAnimate}
          components={citationComponents}
          rehypePlugins={citationRehypePlugins}
        />
      );
    }

    case "file-group":
      return (
        <MessageAttachments>
          {part.parts.map((file, j) => (
            <MessageAttachment
              key={`${role}-${i}-${j}`}
              isPublic={!!publicMetadata}
              data={{
                type: "file",
                mediaType: file.mediaType,
                filename: file.filename,
                url: file.url,
                providerMetadata: file.providerMetadata,
              }}
            />
          ))}
        </MessageAttachments>
      );

    case "reasoning":
      // Narration is something the agent wrote for the user, so it reads as a
      // plain row — a thought gets an expander because its body is private
      // detail; narration has no body to open.
      if (isNarrationPart(part)) {
        if (!groupIntoChainOfThought) {
          return (
            <div className={AGENT_PROSE_SPACING} data-testid="narration">
              <MessageResponse
                className="leading-relaxed"
                components={citationComponents}
                rehypePlugins={citationRehypePlugins}
              >
                {part.text.trim()}
              </MessageResponse>
            </div>
          );
        }
        return (
          <div
            className={cn(
              "whitespace-pre-line text-muted-foreground",
              CHAIN_TEXT_SIZE
            )}
            data-testid="narration"
          >
            {part.text.trim()}
          </div>
        );
      }
      if (hideReasoning || isEmptyThought(part)) return null;
      return (
        <Reasoning isStreaming={part.state === "streaming"} defaultOpen={false}>
          <ReasoningTrigger
            title={
              reasoningTitle(part.text, part.state === "streaming") ?? undefined
            }
          />
          {/* Same rail as a tool row's detail: the indent belongs to the step,
              not to the kind of body it opens. */}
          <ReasoningContent className={cn("mt-1.5", STEP_DETAIL_INDENT)}>
            {part.text}
          </ReasoningContent>
        </Reasoning>
      );

    case "dynamic-tool":
      return (
        <div className="text-destructive border-destructive border p-2 rounded-md">
          Unsupported dynamic tool: {part.toolName}
        </div>
      );

    case "source-group": {
      if (citationStyle === "hidden") return null;
      return (
        <StandaloneCitation
          sources={part.parts}
          citationStyle={citationStyle}
          omitCitationQuotes={omitCitationQuotes}
        />
      );
    }

    case "data-error": {
      // A deployed agent's users can neither read nor act on a raw failure,
      // and the reply already says what could not be done. The setting only
      // applies on the deployed widget (publicMetadata): the platform's own
      // chat is where the builder debugs, so the banner always shows there.
      // Usage limits are the exception everywhere: that one is addressed to
      // whoever owns the account, and hiding it leaves them with a silently
      // dead agent (DEV-2363).
      const isUsageLimitError = part.data?.code === "payment_required";
      const hideFromEndUser =
        hideErrorBanners && !!publicMetadata && !isUsageLimitError;
      // Only the reply can stand in for the banner, so there has to be one. A
      // turn that dies before the model speaks — the context-window guard, a
      // provider refusal, a guardrail block — carries nothing but this part,
      // and hiding it leaves an empty bubble with no reason and no retry.
      const replyStandsOnItsOwn = allParts.some(
        (other) =>
          other.type === "text" &&
          typeof other.text === "string" &&
          other.text.trim() !== ""
      );
      if (hideFromEndUser && replyStandsOnItsOwn) {
        return null;
      }
      // Shown to an end user only because nothing else was: they get the
      // generic copy and a retry, never the provider's words.
      const detail = hideFromEndUser ? undefined : part.data;
      // A deployed agent's users do not own the account: they get neither the
      // usage figures nor a way to act on them.
      const publicUsageLimit = isUsageLimitError && !!publicMetadata;
      // The organization's own usage control is raised by its admins, not by
      // Noxus support.
      const organizationLimit =
        isUsageLimitError && !!part.data?.limit?.is_internal_limit;
      // Clears by itself once in-flight work finishes: retrying is the only step.
      const heldByUnfinishedWork =
        isUsageLimitError && !!part.data?.limit?.held_by_unfinished_work;
      const handleErrorPylon: MouseEventHandler<HTMLButtonElement> =
        isUsageLimitError
          ? (event) => {
              event.stopPropagation();
              triggerPylonMessage(
                `My account has reached its usage limit and my agents can't reply. ` +
                  `TenantID: ${spotTenant?.id}. ` +
                  `Could you help me review my account's limits?`
              );
            }
          : handlePylon;
      return (
        <Alert variant="destructive">
          <AlertTitle>
            {publicUsageLimit
              ? t("usageLimitTitle")
              : (detail?.title ?? t("errorTitle"))}
          </AlertTitle>
          <AlertDescription>
            <div className="whitespace-pre-line">
              {publicUsageLimit
                ? t("usageLimitDescription")
                : (detail?.message ?? t("errorDescription"))}
            </div>
            <div className="flex gap-2 justify-between w-full mt-2">
              {regenerate ? (
                <Button
                  variant="ghostDestructive"
                  className="px-0"
                  onClick={() => regenerate({ messageId: id })}
                >
                  <RefreshCcwIcon className="size-4" />
                  {t("retryMessage")}
                </Button>
              ) : (
                <span />
              )}
              {publicUsageLimit ||
              heldByUnfinishedWork ? null : organizationLimit ? (
                <ManageUsageControlsButton label={t("manageUsageControls")} />
              ) : (
                <Button
                  onClick={handleErrorPylon}
                  variant="ghostDestructive"
                  className="px-0"
                >
                  <MessageCircle className="size-4" />
                  {t("speakWithUs")}
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      );
    }

    default: {
      if (part.type.startsWith("data-")) {
        return renderDataPart(part, `${role}-${i}`, !!publicMetadata, {
          groupId,
          conversationId,
        });
      }
      if (part.type.startsWith("tool-")) {
        if (toolDisplayPreset === "minimal") return null;
        return renderToolPart(
          part as ToolPartBase,
          allParts,
          `${role}-${i}`,
          groupId,
          mcpPrefixes,
          !!publicMetadata
        );
      }
      return null;
    }
  }
});

export const NoxusMessageContent = memo(function NoxusMessageContent({
  publicMetadata,
  parts,
  role,
  groupId,
  agentId,
  conversationId,
  index,
  messages,
  regenerate,
  status,
  id,
  dbMessageId,
  setSourcesDrawerOpen,
  senderRef,
  channelType,
  workedMs,
}: {
  parts: MessagePart[];
  role: NoxusUIMessage["role"];
  groupId: string;
  agentId: string;
  conversationId: string;
  index: number;
  messages: NoxusUIMessage[];
  regenerate: (options?: { messageId?: string }) => any;
  status: ChatStatus;
  id: string;
  /** The DB row this message was saved under, from the message metadata. */
  dbMessageId?: string;
  setSourcesDrawerOpen: (
    sources: (SourceUrlUIPart | SourceDocumentUIPart)[]
  ) => void;
  publicMetadata?: {
    group_id: string;
    token: string;
    deployment_id?: string;
  };
  senderRef?: SenderRefSchema | null;
  channelType?: string;
  workedMs?: number;
}) {
  const displayOptions = useChatDisplayOptionsContext();
  const {
    toolDisplayPreset,
    citationStyle,
    omitCitationQuotes,
    hideReasoning,
    groupIntoChainOfThought,
    minimalToolMessage,
    minimalCaretOnly,
    isCompact,
    hideActions,
    offerTestThisReply,
    showMessageFeedback,
    messageFooter,
  } = displayOptions;
  const tChain = useTranslations("widget.chain");
  const reasoningTexts = useMemo(
    () => ({ during: tChain("thinking"), after: tChain("thought") }),
    [tChain]
  );
  const mcpPrefixes = useMcpPrefixMeta();
  const partsWithIndex = useMemo(
    () =>
      parts
        .filter((p) => !isSourceImagePart(p))
        .map((p, i) => ({ ...p, _index: i })),
    [parts]
  );

  const isLastMessage = index === messages.length - 1;
  const isMessageStreaming =
    role !== "user" &&
    isLastMessage &&
    (status === "streaming" || status === "submitted");

  // Stopping/aborting the stream mid-turn means the reasoning-end chunk (or
  // tool result) never arrives, leaving parts stuck in an active state —
  // settle them so the thinking shimmer and tool/chain spinners don't hang
  // forever. Approval states are deliberately untouched: a pending approval
  // card must stay actionable while the chat is idle.
  const settledParts = useMemo(() => {
    if (isMessageStreaming) return partsWithIndex;
    return partsWithIndex.map((p) => {
      if (p.type === "reasoning" && p.state === "streaming") {
        return { ...p, state: "done" as const };
      }
      if (
        p.type.startsWith("tool-") &&
        "state" in p &&
        p.state !== undefined &&
        ACTIVE_TOOL_STATES.has(p.state)
      ) {
        // "complete" is an extended ToolState (see ToolPartBase), not part
        // of the SDK part union — hence the conversion through unknown.
        return { ...p, state: "complete" } as unknown as typeof p;
      }
      return p;
    });
  }, [partsWithIndex, isMessageStreaming]);

  const groupedParts = useMemo(
    () => groupConsecutiveParts(settledParts),
    [settledParts]
  );

  const { processedParts, citationSources } = useMemo(
    () => preprocessCitationParts(groupedParts, citationStyle),
    [groupedParts, citationStyle]
  );

  type RenderEntry =
    | { kind: "single"; part: GroupedPart; index: number }
    | {
        kind: "chain";
        parts: { part: GroupedPart; index: number }[];
        startIndex: number;
      };

  const entries = useMemo<RenderEntry[]>(() => {
    if (role !== "assistant" || !groupIntoChainOfThought) {
      return processedParts.map((part, index) => ({
        kind: "single",
        part,
        index,
      }));
    }

    const result: RenderEntry[] = [];
    let currentChain: { part: GroupedPart; index: number }[] = [];
    let chainStart = -1;
    const flushChain = () => {
      if (currentChain.length > 0) {
        result.push({
          kind: "chain",
          parts: currentChain,
          startIndex: chainStart,
        });
        currentChain = [];
        chainStart = -1;
      }
    };

    for (let index = 0; index < processedParts.length; index++) {
      const part = processedParts[index];
      // Mirror the today-behavior of `case "reasoning": if (hideReasoning) return null;`
      // so a hidden reasoning part doesn't appear as a chain step or break a chain run.
      // Narration is the agent's own status text, not model thinking, so hiding
      // reasoning must not take it with it.
      if (part.type === "reasoning" && hideReasoning && !isNarrationPart(part))
        continue;
      if (isEmptyThought(part)) continue;
      // Vercel AI SDK injects step-start parts between model steps during
      // streaming; the API-load path produces no such parts. They render as
      // null but would otherwise split one logical chain into multiple.
      if (part.type === "step-start") continue;
      // Tool-metadata data parts (data-run_workflow etc.) render nothing —
      // tool renderers consume them via findDataPart — but they interleave
      // with their tool parts in the stream and would split the chain.
      if (TOOL_METADATA_DATA_PARTS.has(part.type)) continue;
      // Todo bookkeeping calls render nothing (see toolDisplay) and pepper
      // the stream — skipping keeps them from splitting one chain into many.
      if (
        part.type.startsWith("tool-") &&
        isHiddenToolPart(part as ToolPartBase)
      )
        continue;

      // Text is never chained: it closes the chain, renders as prose, and the
      // next step opens a fresh chain. Whether the agent's mid-turn words are
      // prose or a folded step is its own call, made with the final-answer
      // marker — never guessed from where the part happens to sit.
      if (isChainablePart(part, mcpPrefixes)) {
        if (currentChain.length === 0) chainStart = index;
        currentChain.push({ part, index });
      } else {
        flushChain();
        result.push({ kind: "single", part, index });
      }
    }
    flushChain();
    return result;
  }, [
    processedParts,
    role,
    groupIntoChainOfThought,
    hideReasoning,
    mcpPrefixes,
  ]);

  const hasToolParts = processedParts.some((p) => p.type.startsWith("tool-"));

  const waitingFor = useMemo((): "approval" | "answer" | null => {
    if (
      parts.some(
        (part) =>
          part.type.startsWith("tool-") &&
          "state" in part &&
          isActionablePendingApproval(part)
      )
    )
      return "approval";
    return parts.some(
      (part) =>
        part.type === QUESTION_PART_TYPE &&
        "state" in part &&
        part.state === "input-available"
    )
      ? "answer"
      : null;
  }, [parts]);

  const answerStarted = useMemo(
    () => hasAnswerStarted(processedParts),
    [processedParts]
  );

  const isWorking = isMessageStreaming && !answerStarted && waitingFor === null;

  const lastChainStart = useMemo(() => {
    const chains = entries.filter((entry) => entry.kind === "chain");
    return chains.length > 0 ? chains[chains.length - 1].startIndex : -1;
  }, [entries]);

  const showsNothingYet =
    isMessageStreaming &&
    !entries.some((entry) =>
      entry.kind === "chain"
        ? toolDisplayPreset !== "minimal"
        : rendersSomething(entry.part, citationStyle, mcpPrefixes)
    ) &&
    !(toolDisplayPreset === "minimal" && hasToolParts);

  const showsWorkInFlight =
    (toolDisplayPreset === "minimal" && hasToolParts) ||
    entries.some((entry) => entry.kind === "chain") ||
    processedParts.some((part) =>
      part.type.startsWith("tool-")
        ? "state" in part &&
          ACTIVE_TOOL_STATES.has((part as ToolPartBase).state)
        : part.type === "reasoning" && part.state === "streaming"
    );

  const showsStillWorking = isWorking && !showsNothingYet && !showsWorkInFlight;

  const citationComponents = useMemo(
    () => ({ cite: CitationComponent }) as StreamdownProps["components"],
    []
  );
  const citationRehypePlugins = useMemo(
    () => [...Object.values(defaultRehypePlugins), rehypeCitationMarkers],
    []
  );

  const handlePylon: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.stopPropagation();
      triggerPylonMessage(
        `An error occurred in conversation. ConversationID: ${conversationId} | GroupID: ${groupId}`
      );
    },
    [conversationId, groupId]
  );

  const sharedPartProps = useMemo<SharedPartProps>(
    () => ({
      role,
      allParts: parts,
      groupId,
      conversationId,
      id,
      publicMetadata,
      senderRef,
      citationComponents,
      citationRehypePlugins,
      regenerate: isLastMessage ? regenerate : undefined,
      handlePylon,
      mcpPrefixes,
    }),
    [
      role,
      conversationId,
      parts,
      groupId,
      id,
      publicMetadata,
      senderRef,
      citationComponents,
      citationRehypePlugins,
      isLastMessage,
      regenerate,
      handlePylon,
      mcpPrefixes,
    ]
  );

  return (
    <Message
      from={role}
      compact={isCompact}
      className={index === 0 ? "mt-5" : undefined}
      // The DB row id lets the feedback comments panel scroll to and
      // highlight the reply a feedback card points at. Replies only: a row
      // that holds a prompt and its reply renders both, and ratings are on
      // the reply.
      data-db-message-id={role === "assistant" ? dbMessageId : undefined}
    >
      {senderRef?.external_participant && role === "user" && (
        <ExternalSenderBadge
          participant={senderRef.external_participant}
          channelType={channelType}
        />
      )}
      <CitationSourcesContext.Provider
        value={{ sources: citationSources, citationStyle, omitCitationQuotes }}
      >
        <MessageContent>
          {showsNothingYet && (
            <ThinkingLine label={thinkingLabel(displayOptions) ?? undefined} />
          )}
          {toolDisplayPreset === "minimal" &&
            hasToolParts &&
            isMessageStreaming && (
              <MinimalToolsIndicator
                message={minimalCaretOnly ? minimalToolMessage : undefined}
              />
            )}
          {entries.map((entry) => {
            if (entry.kind === "chain") {
              if (toolDisplayPreset === "minimal") return null;
              const chainParts = entry.parts.map((p) => p.part);
              const headerText = getChainHeaderText(
                chainParts,
                mcpPrefixes,
                parts,
                reasoningTexts
              );
              const headerStatus = getChainHeaderStatus(chainParts);
              return (
                <ChainEntry
                  key={`${role}-cot-${entry.startIndex}`}
                  headerText={headerText}
                  status={headerStatus}
                  preview={getChainPreview(chainParts)}
                  isProcessing={isWorking}
                  waitingFor={waitingFor}
                  isLastInTurn={entry.startIndex === lastChainStart}
                  workedMs={workedMs}
                >
                  <div
                    className={cn(
                      "ml-2 space-y-2 border-l border-border pl-4 text-muted-foreground",
                      CHAIN_TEXT_SIZE
                    )}
                  >
                    {entry.parts.map(({ part, index }) => (
                      <div key={`${role}-${index}`} className="my-3">
                        <RenderedPart
                          part={part}
                          index={index}
                          shouldAnimate={false}
                          {...sharedPartProps}
                        />
                      </div>
                    ))}
                  </div>
                </ChainEntry>
              );
            }

            const { part, index: i } = entry;
            const isLastPart = i === processedParts.length - 1;
            const shouldAnimate = isMessageStreaming && isLastPart;
            return (
              <RenderedPart
                key={`${role}-${i}`}
                part={part}
                index={i}
                shouldAnimate={shouldAnimate}
                {...sharedPartProps}
              />
            );
          })}
          {showsStillWorking && (
            <ThinkingLine label={thinkingLabel(displayOptions) ?? undefined} />
          )}
        </MessageContent>
      </CitationSourcesContext.Provider>
      {role === "assistant" && (
        <SourceImageAttachments parts={parts} isPublic={!!publicMetadata} />
      )}
      {role === "assistant" && <ImageThumbnails parts={parts} />}
      {role === "assistant" && !isMessageStreaming && !!messageFooter && (
        <MessageResponse className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {messageFooter}
        </MessageResponse>
      )}
      {((!publicMetadata && !hideActions) ||
        (!!publicMetadata && showMessageFeedback)) && (
        <NoxusMessageActions
          isLastMessage={index === messages.length - 1}
          status={status}
          parts={parts}
          role={role}
          messageId={id}
          dbMessageId={dbMessageId}
          messageIndex={index}
          messages={messages}
          regenerate={() => regenerate({ messageId: id })}
          groupId={groupId}
          agentId={agentId}
          conversationId={conversationId}
          setSourcesDrawerOpen={setSourcesDrawerOpen}
          publicMetadata={publicMetadata}
          feedbackOnly={!!publicMetadata}
        />
      )}
      {offerTestThisReply && hideActions && role === "assistant" && (
        <TestThisReplyAction
          standalone
          agentId={agentId}
          conversationId={conversationId}
          dbMessageId={dbMessageId}
          parts={parts}
          messageIndex={index}
          messages={messages}
        />
      )}
    </Message>
  );
});
